import FormControl from '@mui/material/FormControl';
import { ReactElement, useCallback } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import PhoneInput from 'react-phone-number-input';

import { FormFieldLabel } from '@/components/form/FormFieldLabel.comp.tsx';
import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';

import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

interface PhoneFormFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends FormPropsModel<TName> {
  value?: string;
}

export const PhoneFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: PhoneFormFieldProps<TFieldValues, TName>,
): ReactElement<PhoneFormFieldProps<TFieldValues, TName>> => {
  const { control } = useFormContext<TFieldValues>();
  const { fullWidth, id, label, name, placeholder, ...rest } = props;
  const { validation } = useFormProviderRCPropsContext();
  const render: ControllerProps<TFieldValues, TName>['render'] = useCallback(
    ({ field, fieldState: { error } }) => {
      const isRequired = isFormFieldRequired(field.name, validation);

      return (
        <FormControl fullWidth={fullWidth ?? true} sx={{ pt: 2 }}>
          <FormFieldLabel isRequired={isRequired} label={label} sx={{ pt: 2 }} />
          <PhoneInput
            defaultCountry="PL"
            {...field}
            {...rest}
            countryOptionsOrder={['PL', 'US', 'GB', 'CA', 'AU', 'DE', 'FR', 'IT', 'ES', 'IN']}
            id={id && `input-${id}`}
            initialValueFormat="national"
            key={name}
            numberInputProps={{
              style: {
                border: `1px solid ${themeColors.stroke}`,
                borderRadius: '4px',
                height: '100%',
                padding: '12px 16px',
              },
            }}
            placeholder={placeholder ?? label}
            style={{ height: '46px' }}
          />
          <TextError error={error} />
        </FormControl>
      );
    },
    [validation, fullWidth, rest, label, id, name, placeholder],
  );

  return (
    <>
      <Controller<TFieldValues, TName> control={control} name={name} render={render} />
    </>
  );
};
